import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';

import { PortalApiRepository } from '../../../../core/repositories/portal-api-repository';
import { CurrentUserState } from '../../../../core/store/current-user/current-user.state';
import {
  CertificateBundleExportRequest,
  CertificateExportRequest,
  CertificateTrainingPeriod,
  UserCertificateBundleExportRequest,
} from '../models/CertificateExportRequest';
import { MissingSubscriptionForCertificateExportError } from '../models/CertificateExportErrors';
import { CertificateLanguage } from '../models/CertificateLanguage';

interface CertificateExportBody {
  language: CertificateLanguage;
  startDate?: string;
  endDate?: string;
}

interface CertificateBundleExportBody extends CertificateExportBody {
  userIds: string[];
}

@Injectable({
  providedIn: 'root',
})
export class CertificateService extends PortalApiRepository {
  constructor() {
    super();
  }

  getCertificate(
    request: CertificateExportRequest,
  ): Observable<HttpResponse<Blob>> {
    return this.downloadFromSubscription(
      `Courses/${request.courseId}/Certificates/${request.userId}`,
      this.toBody(request.language, request.period),
    );
  }

  getCertificateBundle(
    request: CertificateBundleExportRequest,
  ): Observable<HttpResponse<Blob>> {
    return this.downloadFromSubscription<CertificateBundleExportBody>(
      `LearningPlanInstances/${request.learningPlanInstanceId}/Certificates/bundle`,
      {
        userIds: request.userIds,
        ...this.toBody(request.language, request.period),
      },
    );
  }

  getUserCertificateBundle(
    request: UserCertificateBundleExportRequest,
  ): Observable<HttpResponse<Blob>> {
    return this.downloadFromSubscription(
      `Users/${request.userId}/Certificates/bundle`,
      this.toBody(request.language),
    );
  }

  private downloadFromSubscription<B extends CertificateExportBody>(
    pathInSubscription: string,
    body: B,
  ): Observable<HttpResponse<Blob>> {
    const subscriptionId = this.store.selectSnapshot<string | undefined>(
      CurrentUserState.subscriptionId,
    );
    if (!subscriptionId) {
      return throwError(
        () => new MissingSubscriptionForCertificateExportError(),
      );
    }

    return this.downloadWithResponse(
      `/subscriptions/${subscriptionId}/${pathInSubscription}`,
      body,
    );
  }

  private toBody(
    language: CertificateLanguage,
    period?: CertificateTrainingPeriod,
  ): CertificateExportBody {
    if (!period) {
      return { language };
    }
    return {
      language,
      startDate: period.startDate,
      endDate: period.endDate,
    };
  }
}
